import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import {
  Dialog,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Card,
  Checkbox
} from '@mui/material';

import {
  MDBox,
  MDTypography,
  Chart,
  ShareChartModal,
  ConfirmationModal,
  AuthGuard,
  FaIcon,
  CloseButton,
  DownloadPreviewModal,
  UpDownVote
} from '@components/index';
import DataTableModal from '../DataTableModal';

import { RootState } from '@store/store';
import {
  useLazyGetChartDetailsQuery,
  useLazyGetChartLinkQuery
} from '@api/chart';
import { setOpenInsights } from '@pages/EditChart/slice';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { ChartConfigType, ChartCreationOptions } from '@DStypes/chart';

import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import { getPageName } from '@utils/common';
import { getFilterTooltipText } from '@utils/chart';
import useCharts from '@hooks/useCharts';
import useCustomNavigate from '@hooks/useCustomNavigate';
import {
  DELETE_SINGLE_CHART,
  EDIT_CHART_CLICKED,
  PAGE_VISITED,
  CLICKED_SHARE_CHART,
  CLICKED_TABLE_ICON,
  CLICKED_SHOW_INSIGHT
} from '@constants/eventNames';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  FeatureTypes,
  numberEquivalents
} from '@constants/common';

import './styles.scss';

interface Props {
  chartConfig: ChartConfigType;
  projectColorSet?: string[] | null;
  isSelected?: boolean;
  onCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onDeleteChartById?: () => void;
  isBoardPreview: boolean;
  disableDrag?: boolean;
  isGoAutoEnabled: boolean;
  chartLoadedCallback: () => void;
  availableFields: any[];
  onClickRemoveWaterMark: () => void;
  subscriptionFlag: boolean;
  isChartInsightsEnabledByPlan: boolean;
}

const ChartCard = ({
  chartConfig,
  projectColorSet,
  isSelected,
  onCheckboxChange,
  onDeleteChartById = () => null,
  isBoardPreview,
  disableDrag,
  isGoAutoEnabled,
  chartLoadedCallback,
  onClickRemoveWaterMark,
  availableFields,
  subscriptionFlag,
  isChartInsightsEnabledByPlan
}: Props) => {
  const navigate = useCustomNavigate();
  const dispatch = useDispatch();
  const { collaborationId } = useParams();

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const { deleteChart } = useCharts({ chartId: chartConfig.id });
  const [
    getChartDetails,
    { data: dropdownData, isLoading: isChartDetailsLoading }
  ] = useLazyGetChartDetailsQuery();
  const [getChartLink, { data: chartLink, isLoading, isFetching }] =
    useLazyGetChartLinkQuery();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const xAxisType = chartConfig?.config?.x_axis?.treat_as_type;
  const yAxisType = chartConfig?.config?.y_axis?.treat_as_type;
  const chartType = chartConfig.config.chart_type;
  const { current: renderChartsAsSvg } = useRef<boolean>(
    isBoardPreview && window.innerWidth < 1000
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [shareChartModalOpen, setShareChartModalOpen] =
    useState<boolean>(false);
  const [showDownloadPreview, setShowDownloadPreview] =
    useState<boolean>(false);
  const [showDataTable, setShowDataTable] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState(false);
  const [chartRating, setChartRating] = useState(chartConfig?.rating || 0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const chartData = useMemo(
    () => projectBoard.data?.[chartConfig.id]?.data,
    [projectBoard.data?.[chartConfig.id]]
  );

  const config = useMemo(() => {
    if (chartConfig) return JSON.parse(chartConfig.config.chart_config);

    return null;
  }, [chartConfig]);

  const getFilterTooltip = () => (
    <div style={{ textAlign: 'left' }}>
      {getFilterTooltipText(
        chartConfig.config.filters || {},
        chartConfig.config.dynamic_filters || {},
        availableFields,
        false,
        '',
        chartConfig.config.date_range
      )}
    </div>
  );

  const showFilterIcon = useMemo(() => {
    const projecFilters =
      Object.keys(projectBoard?.currentProjectData?.filters || {}).length > 0 ||
      Object.keys(projectBoard?.currentProjectData?.dynamic_filters || {})
        .length > 0
        ? true
        : false;

    const projectFilterColumns = Object.keys(
      projectBoard?.currentProjectData?.filters || []
    ).concat(
      Object.keys(projectBoard?.currentProjectData?.dynamic_filters || {})
    );

    const chartchartGlobalFilterToggle =
      chartConfig?.config?.visual_settings?.chartGlobalFilterToggle;

    const toggleDashboardFilter =
      chartConfig?.config?.visual_settings?.toggleDashboardFilter;

    const chartFilterArray = Object.entries(chartConfig.config.filters);

    const chartDynamicFilterArray = Object.entries(
      chartConfig.config.dynamic_filters || {}
    );

    const activeChartFilters = chartFilterArray
      .map(([key, obj]: any) => {
        if (obj.active === 'true') return key;
      })
      .filter((value: any) => value !== undefined);

    const activeChartDynamicFilters = chartDynamicFilterArray
      .map(([key, obj]: any) => {
        if (obj.active === 'true') return key;
      })
      .filter((value: any) => value !== undefined);

    if (projecFilters)
      if (toggleDashboardFilter) return { value: false, tooltip: '' };
      else if (!toggleDashboardFilter)
        return {
          value: true,
          tooltip:
            activeChartFilters.length || activeChartDynamicFilters.length
              ? getFilterTooltip()
              : `${projectFilterColumns?.join(', ')} ${
                  projectFilterColumns?.length > 1 ? 'filters are' : 'filter is'
                } not applied to this chart`
        };
      else if (typeof chartchartGlobalFilterToggle === 'undefined')
        return { value: false, tooltip: '' };
      else return { value: true, tooltip: 'No filter applied to this chart' };
    else if (chartchartGlobalFilterToggle)
      if (activeChartFilters?.length || activeChartDynamicFilters?.length)
        return {
          value: true,
          tooltip: getFilterTooltip()
        };
      else return { value: false, tooltip: '' };
    else return { value: false, tooltip: '' };
  }, [
    projectBoard?.currentProjectData?.filters,
    projectBoard?.currentProjectData?.dynamic_filters,
    chartConfig,
    availableFields
  ]);

  const colorSet = useMemo(() => {
    if (
      chartConfig?.config?.visual_settings?.theme_connection_status
        ?.colorThemeToggle
    )
      return projectColorSet || null;

    if (chartConfig?.config?.visual_settings?.colorThemeToggle)
      return chartConfig?.colorset || null;

    return null;
  }, [
    chartConfig?.config?.visual_settings,
    chartConfig.config.chart_config,
    chartType,
    projectColorSet
  ]);

  const [debouncedColorSet] = useDebounce(colorSet, 300);

  const getChart = useMemo<JSX.Element | null>(() => {
    const isSelfChart =
      chartConfig?.config?.x_axis?.id === chartConfig?.config?.y_axis?.id;

    const isNumber = numberEquivalents.includes(xAxisType as string);

    const isYPercentage = yAxisType === 'PERCENTAGE' ? true : false;

    const isXPercentage = xAxisType === 'PERCENTAGE' ? true : false;

    const xAxisField = chartConfig?.config?.x_axis?.fieldname || '';

    const activeDateGroup =
      chartConfig?.config.date_group === 'none'
        ? xAxisType === 'DATE'
          ? DATE_TIME_GROUP.DAYS
          : DATE_TIME_GROUP.HOURS
        : chartConfig?.config.date_group;

    if (chartData && chartConfig)
      return (
        <Chart
          chartId={chartConfig.id}
          data={chartData}
          chartConfig={JSON.parse(chartConfig.config.chart_config)}
          chartType={chartType}
          hideAxisLabels={true}
          visualSettings={chartConfig.config.visual_settings}
          isProjectBoard
          isSegmented={chartConfig.config.segment_field}
          height='97.5%'
          colorSet={debouncedColorSet || null}
          dateGroup={activeDateGroup}
          isDate={
            chartConfig.config?.x_axis?.treat_as_type === 'DATE' ||
            chartConfig.config?.x_axis?.treat_as_type === 'DATETIME'
          }
          xDateItemsToRender={5}
          renderAsSvg={renderChartsAsSvg}
          isSelfChart={isSelfChart}
          isNumber={isNumber}
          title={chartConfig.chart_title}
          chartOperation={chartConfig.config.chart_operation}
          handleOnChartReady={chartLoadedCallback}
          defaultDateGroup={chartConfig.config.default_date_group}
          isYPercentage={isYPercentage}
          isXPercentage={isXPercentage}
          skipEmptyPeriods={chartConfig?.config.date_group === 'none'}
          isBoardPreview={isBoardPreview}
          xAxisField={xAxisField}
        />
      );

    return null;
  }, [chartData, chartConfig, debouncedColorSet]);

  const getPopupChart = useMemo<JSX.Element | null>(() => {
    const isSelfChart =
      chartConfig?.config?.x_axis?.id === chartConfig?.config?.y_axis?.id;

    const isNumber = numberEquivalents.includes(xAxisType as string);

    const isYPercentage = yAxisType === 'PERCENTAGE' ? true : false;

    const isXPercentage = xAxisType === 'PERCENTAGE' ? true : false;

    const xAxisField = chartConfig?.config?.x_axis?.fieldname || '';

    const activeDateGroup =
      chartConfig?.config.date_group === 'none'
        ? xAxisType === 'DATE'
          ? DATE_GROUP.DAYS
          : DATE_TIME_GROUP.HOURS
        : chartConfig?.config.date_group;

    if (isPreviewOpen && chartData && chartConfig)
      return (
        <Chart
          chartId={chartConfig.id ? `${chartConfig.id}-popup` : ''}
          data={chartData}
          chartConfig={JSON.parse(chartConfig.config.chart_config)}
          chartType={chartType}
          hideAxisLabels={true}
          height='250px'
          visualSettings={chartConfig.config.visual_settings}
          isProjectBoard
          isSegmented={chartConfig.config.segment_field}
          colorSet={debouncedColorSet || null}
          dateGroup={activeDateGroup}
          isDate={chartConfig.config?.x_axis?.treat_as_type === 'DATE'}
          xDateItemsToRender={5}
          isSelfChart={isSelfChart}
          isNumber={isNumber}
          needDequeuedChart
          isYPercentage={isYPercentage}
          isXPercentage={isXPercentage}
          skipEmptyPeriods={chartConfig?.config.date_group === 'none'}
          xAxisField={xAxisField}
        />
      );

    return null;
  }, [isPreviewOpen]);

  useEffect(() => {
    if (chartLink) navigator.clipboard.writeText(chartLink as string);
  }, [isLoading, isFetching]);

  const onCloseShareChartModal = () => {
    setShareChartModalOpen(false);
  };

  const onConfirmDelete = () => {
    sendAmplitudeData(DELETE_SINGLE_CHART);
    deleteChart();
    onDeleteChartById();
  };

  const handleNavigate = () => {
    if (isBoardPreview) {
      navigate(
        `/chart/${chartConfig.id}/${collaborationId}/${chartConfig.chart_project}/preview`
      );
    } else {
      sendAmplitudeData(EDIT_CHART_CLICKED, {
        'Data Type Combination': `${xAxisType} & ${yAxisType}`
      });
      navigate(`/chart/${chartConfig.id}/edit`, {
        isGoAutoEnabled: isGoAutoEnabled
      });
    }
  };

  const handleFilterIconClick = () => {
    navigate(`/chart/${chartConfig.id}/edit`, {
      isGoAutoEnabled: isGoAutoEnabled,
      openFilter: true
    });
  };

  return (
    <>
      <Card
        // ref={divRef}
        className='chart-card'
        sx={{
          border: ({ palette: { primary } }) =>
            `2px solid ${isSelected ? primary.main : 'transparent'}`,
          overflow: 'hidden',
          position: 'relative',
          width: renderChartsAsSvg ? '95vw' : '100%',
          height: '100%',
          boxShadow: '0 0 30px rgba(0, 0, 0, 0.08)',
          minHeight: renderChartsAsSvg ? '280px' : ''
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <MDBox display={'flex'} alignItems={'baseline'} paddingLeft={'7px'}>
          {!isBoardPreview && (
            <MDBox>
              {showFilterIcon.value && (
                <Grid item onClick={handleFilterIconClick}>
                  <Tooltip title={showFilterIcon.tooltip} placement='top'>
                    <IconButton color='secondary'>
                      <FaIcon
                        typographyProps={{ variant: 'lg' }}
                        icon='filter'
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </MDBox>
          )}
          <MDBox
            display='flex'
            alignItems='baseline'
            justifyContent='space-between'
            className='drag-handle'
            sx={{ width: '100%' }}
          >
            <Tooltip
              title={
                <>
                  <div>{chartConfig.chart_title}</div>
                  {chartConfig.chart_short_desc && (
                    <>
                      <br />
                      <div>{chartConfig.chart_short_desc}</div>
                    </>
                  )}
                  {chartConfig.chart_long_desc && (
                    <>
                      <br />
                      <div>{chartConfig.chart_long_desc}</div>
                    </>
                  )}
                </>
              }
              placement='top'
            >
              <MDTypography
                variant='h5'
                textGradient
                color='secondary'
                px={1.5}
                mt={isBoardPreview ? 1.5 : 1.2}
                noWrap
                sx={{
                  whiteSpace: 'normal',
                  display: '-webkit-box',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  WebkitLineClamp: { xs: 3, md: 2 },
                  WebkitBoxOrient: 'vertical',
                  maxWidth: { xs: '90%', lg: '95%' },
                  cursor: 'pointer'
                }}
                onClick={handleNavigate}
              >
                {chartConfig.chart_title}
              </MDTypography>
            </Tooltip>
            {isBoardPreview && (
              <MDBox display={{ xs: 'flex', lg: 'none' }} sx={{ gap: 2 }}>
                <FaIcon
                  icon='eye'
                  size='sm'
                  style={{
                    paddingRight: 1
                  }}
                  typographyProps={{ color: 'secondary' }}
                  onClick={() => setIsPreviewOpen(true)}
                />
                <FaIcon
                  icon='arrow-right'
                  size='sm'
                  style={{
                    paddingRight: 1
                  }}
                  typographyProps={{ color: 'secondary' }}
                  onClick={handleNavigate}
                />
              </MDBox>
            )}
          </MDBox>
        </MDBox>
        {!isBoardPreview && (
          <Grid
            container
            alignItems={'center'}
            className='chart-card-outerheader'
            justifyContent={'space-between'}
            sx={{
              position: 'absolute',
              borderRadius: '0.55rem',
              bottom: 0,
              paddingX: '10px',
              paddingY: '1px',
              zIndex: 50
            }}
          >
            <Grid container item xs={5} sx={{ pointerEvents: 'auto' }}>
              <Grid
                className='chart-card-header'
                display='flex'
                flexDirection='row'
              >
                <Grid item className={isSelected ? '' : 'chart-card-header'}>
                  <Tooltip
                    title='Enable checkbox and select many charts at once to perform an action together. Or perform the action individually.'
                    placement='top'
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={onCheckboxChange}
                    />
                  </Tooltip>
                </Grid>
                <Divider
                  orientation='vertical'
                  sx={{
                    margin: '6px 4px 0px 2px',
                    height: '70%',
                    backgroundColor: 'black !important'
                  }}
                />
                <Grid
                  className='chart-card-header'
                  display='flex'
                  flexDirection='row'
                >
                  <Grid item>
                    <Tooltip
                      title={`Download only this ${
                        chartType === 'KPI' ? 'KPI' : 'chart'
                      }`}
                      placement='top'
                    >
                      <IconButton
                        color='secondary'
                        onClick={() => setShowDownloadPreview(true)}
                      >
                        <FaIcon
                          icon='download'
                          typographyProps={{ variant: 'lg', color: 'dark' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip
                      title={`Share this ${
                        chartType === 'KPI' ? 'KPI' : 'chart'
                      }`}
                      placement='top'
                    >
                      <IconButton
                        color='secondary'
                        onClick={() => {
                          setShareChartModalOpen(true);
                          sendGAData(CLICKED_SHARE_CHART);
                          sendAmplitudeData(CLICKED_SHARE_CHART);
                          sendAmplitudeData(PAGE_VISITED, {
                            'Page Name': 'Share Chart Modal',
                            Navigation: getPageName()
                          });
                          sendGAData(PAGE_VISITED, {
                            'Page Name': 'Share Chart Modal',
                            Navigation: getPageName()
                          });
                        }}
                      >
                        <FaIcon
                          icon='share'
                          typographyProps={{ variant: 'lg', color: 'dark' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  {chartType !== 'KPI' && (
                    <Tooltip
                      title='Show chart in tabular form'
                      placement='bottom'
                    >
                      <IconButton
                        classes={{ root: 'preventBgClose' }}
                        onClick={() => {
                          sendAmplitudeData(CLICKED_TABLE_ICON);
                          setShowDataTable(true);
                          getChartDetails({
                            id: projectBoard.currentProjectData?.id || '',
                            xId: chartConfig.config.x_axis?.id,
                            yId: chartConfig.config.y_axis?.id,
                            is_kpi: 'False'
                          });
                        }}
                        sx={{
                          padding: '4px !important'
                        }}
                      >
                        <FaIcon
                          icon='table'
                          typographyProps={{ color: 'dark', variant: 'lg' }}
                          style={{
                            pointerEvents: 'none'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  <AuthGuard is_demo_user={userDetails.user.is_demo_user}>
                    <Grid item>
                      <Tooltip title='More options' placement='top'>
                        <IconButton color='secondary'>
                          <FaIcon
                            typographyProps={{ variant: 'lg', color: 'dark' }}
                            icon='ellipsis-vertical'
                          />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </AuthGuard>
                  <Grid item>
                    <Tooltip
                      title={`Delete only this ${
                        chartType === 'KPI' ? 'KPI' : 'chart'
                      }`}
                      placement='top'
                    >
                      <IconButton
                        color='secondary'
                        onClick={() => setOpenDeleteModal(true)}
                      >
                        <FaIcon
                          typographyProps={{ variant: 'lg', color: 'dark' }}
                          icon='trash-can'
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip
                      title={`Edit this ${
                        chartType === 'KPI' ? 'KPI' : 'chart'
                      }`}
                      placement='top'
                    >
                      <IconButton
                        color='secondary'
                        onClick={() => {
                          sendAmplitudeData(EDIT_CHART_CLICKED, {
                            'Data Type Combination': `${xAxisType} & ${yAxisType}`
                          });
                          navigate(`/chart/${chartConfig.id}/edit`, {
                            isGoAutoEnabled: isGoAutoEnabled
                          });
                        }}
                      >
                        <FaIcon
                          typographyProps={{ variant: 'lg' }}
                          icon='pencil'
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title={``} placement='top'>
                      <IconButton
                        color='secondary'
                        onClick={async () => {
                          sendAmplitudeData(CLICKED_SHOW_INSIGHT, {
                            type: 'Chart'
                          });

                          if (subscriptionFlag) {
                            // If feature not accessible by plan then return
                            const feature = isChartInsightsEnabledByPlan
                              ? FeatureTypes.MAX_MONTHLY_INSIGHTS_LIMIT
                              : FeatureTypes.CHART_INSIGHTS;

                            const response = await getFeatureAccess(feature);

                            if (response?.isSuccess) {
                              dispatch(setOpenInsights({ openInsights: true }));
                              navigate(`/chart/${chartConfig.id}/edit`, {
                                isGoAutoEnabled: isGoAutoEnabled
                              });
                            }
                          }
                        }}
                      >
                        <FaIcon
                          typographyProps={{ variant: 'lg', color: 'primary' }}
                          icon='lightbulb-on'
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container item xs={7} justifyContent='flex-end'>
              {isHovered ? (
                <UpDownVote
                  rating={chartRating}
                  chartDetails={{
                    chart_long_desc: chartConfig.chart_long_desc,
                    chart_short_desc: chartConfig.chart_short_desc,
                    chart_title: chartConfig.chart_title
                  }}
                  setRating={setChartRating}
                  chartId={chartConfig.id}
                  amplitudeProps={{
                    data_type_combination: `${xAxisType} & ${yAxisType}`,
                    method_used:
                      chartConfig?.method_used || ChartCreationOptions.SqB,
                    chart_config_count: chartConfig?.chart_config_count
                  }}
                />
              ) : chartRating === 1 ? (
                <FaIcon
                  icon={['fas', 'thumbs-up']}
                  typographyProps={{
                    variant: 'lg',
                    color: 'success'
                  }}
                />
              ) : chartRating === -1 ? (
                <FaIcon
                  icon={['fas', 'thumbs-down']}
                  typographyProps={{
                    variant: 'lg',
                    color: 'error'
                  }}
                />
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        )}

        <MDBox
          id={`${chartConfig?.id}`}
          p={1}
          display='flex'
          flex={1}
          justifyContent='center'
          onClick={isBoardPreview ? () => handleNavigate() : () => null}
          pt='0'
          sx={({ palette }) => ({
            backgroundColor: palette.white.main,
            cursor: 'pointer'
            // pointerEvents: 'none'
          })}
        >
          {getChart}
        </MDBox>
        {!isBoardPreview && !disableDrag && (
          <Grid
            item
            className='chart-card-header'
            sx={{
              position: 'fixed',
              top: 8,
              right: 10
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Tooltip
              title={`Drag to change ${
                chartType === 'KPI' ? "KPI's" : "chart's"
              } position.`}
              placement='top'
            >
              <IconButton color='secondary'>
                <FaIcon
                  typographyProps={{ variant: 'lg' }}
                  icon='up-down-left-right'
                />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        <ShareChartModal
          open={shareChartModalOpen}
          onClose={onCloseShareChartModal}
          onClickGetLink={() => getChartLink(chartConfig.id)}
          chartLink={chartLink}
          chartId={chartConfig.id}
        />
        <ConfirmationModal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onProceedButtonClick={onConfirmDelete}
          title={
            chartType === 'KPI'
              ? 'Delete selected KPI ?'
              : 'Delete selected chart ?'
          }
          subtitle={
            chartType === 'KPI'
              ? 'Are you sure you want to delete selected KPI ?'
              : 'Are you sure you want to delete selected chart ?'
          }
          proceedButtonMessage='YES, DELETE'
          cancelButtonText='NO, CANCEL'
          projectName={chartConfig.chart_title}
        />
        {chartData && chartConfig && showDownloadPreview && (
          <DownloadPreviewModal
            open={showDownloadPreview}
            chartData={chartData}
            chartConfig={config}
            chartType={chartType}
            chartConfigData={chartConfig}
            visualSettings={chartConfig.config.visual_settings}
            chartTitle={chartConfig.chart_title}
            chartDescription={chartConfig.chart_short_desc}
            chartOperation={chartConfig.config.chart_operation}
            chartId={chartConfig.id}
            onClose={() => {
              setShowDownloadPreview(false);
            }}
            colorSet={debouncedColorSet}
            projectName={projectBoard.currentProjectData?.project_name}
            projectId={projectBoard.currentProjectData?.id}
            dateGroup={
              chartConfig.config.date_group === 'none'
                ? chartConfig.config.x_axis?.detected_type === 'DATE'
                  ? DATE_GROUP.DAYS
                  : DATE_TIME_GROUP.HOURS
                : chartConfig.config.date_group
            }
            isSegmented={chartConfig.config.segment_field}
            isDate={chartConfig.config.x_axis?.detected_type === 'DATE'}
          />
        )}
      </Card>

      {isPreviewOpen && (
        <Dialog
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fullWidth
          sx={{
            zIndex: 100,
            '& .MuiPaper-root': {
              maxWidth: '100% !important',
              position: 'relative',
              overflow: 'visible',
              paddingY: 2,
              margin: 1,
              width: 'calc(100vw - 15px)',
              paddingLeft: chartType === 'Bar' ? '50px' : ''
            }
          }}
        >
          <CloseButton onClose={() => setIsPreviewOpen(false)} />
          <MDBox>
            <FaIcon
              icon='close'
              size='xs'
              style={{
                position: 'absolute',
                right: 15,
                paddingTop: '8px',
                cursor: 'pointer'
              }}
              typographyProps={{ color: 'secondary' }}
              onClick={() => setIsPreviewOpen(false)}
            />
            <Tooltip title={chartConfig.chart_title} placement='top'>
              <MDTypography
                variant='h5'
                textGradient
                color='secondary'
                px={1.5}
                pt={isBoardPreview ? 1.5 : 5.5}
                noWrap
                sx={{
                  maxHeight: '72px',
                  whiteSpace: { xs: 'normal', lg: 'nowrap' },
                  display: { xs: '-webkit-box', lg: 'initial' },
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  maxWidth: { xs: '90%', lg: '100%' },
                  marginBottom: 2
                }}
              >
                {chartConfig.chart_title}
              </MDTypography>
            </Tooltip>
            {getPopupChart}
          </MDBox>
        </Dialog>
      )}
      {showDataTable && !isChartDetailsLoading && (
        <DataTableModal
          chartData={projectBoard?.data?.[chartConfig.id].data ?? []}
          chartConfig={chartConfig.config}
          availableFields={availableFields}
          dropdownData={dropdownData}
          onClose={() => {
            setShowDataTable(false);
          }}
          primaryColor={debouncedColorSet?.[1] ?? debouncedColorSet?.[0] ?? ''}
          onClickRemoveWaterMark={onClickRemoveWaterMark}
        />
      )}
    </>
  );
};

export default ChartCard;
